import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WebResponse } from '@app/shared/interfaces/webResponse.interface';
import { Observable } from 'rxjs';
import { PortalRequest } from '../interfaces/portalRequest.interface';
import { environment } from '@env/environment';
import { WorkCompUserInfoUpdateRequest } from '../interfaces/workCompUserInfoUpdateRequest.interface';
import { UserStatusInfo } from '../interfaces/userStatusInfo.interface';
import {PageSearchSortOptions} from '@app/shared/interfaces/pageSearchSortOptions.interface';
import { PaginatedResult } from '@app/shared/interfaces/paginatedResult.interface';
import { AdminInfoUpdateRequest } from '../interfaces/adminInfoUpdateRequest.interface';
import { User } from '@app/core/interfaces';
import { SaveResult } from '@app/shared/interfaces/saveResult.interface';
import {
  ReferralUserInfoUpdateRequest
} from "@app/features/adminDashboard/interfaces/referralUserInfoUpdateRequest.interface";
import {AuthResponse} from "@app/features/login/interfaces/authResponse.interface";
import {Response} from "@app/core/interfaces/response";
import {ReferralUser} from "@app/core/interfaces/user.interface";
import {UserPageRequest} from "@app/features/adminDashboard/interfaces/userPageRequest.interface";

@Injectable({providedIn: 'root'})
export class AdminService {
    private readonly baseUrl: string  = `${environment.apiUrl}/admin`;

    constructor(private http: HttpClient) { }

    getUnconfirmedPortalRequests$(): Observable<WebResponse<PortalRequest[]>> {
        return this.http.get<WebResponse<PortalRequest[]>>(`${this.baseUrl}/portal-requests/unconfirmed`);
    }

    getReferralPortalRequests$(): Observable<WebResponse<PortalRequest[]>> {
      return this.http.get<WebResponse<PortalRequest[]>>(`${this.baseUrl}/portal-requests/referral-changes`);
    }

    approvePortalRequest$(userSubId: string, info: WorkCompUserInfoUpdateRequest): Observable<WebResponse<boolean>> {
      return this.http.post<WebResponse<boolean>>(`${this.baseUrl}/portal-requests/confirm/${userSubId}`, info);
    }

    denyPortalRequest$(userSubId: string): Observable<WebResponse<boolean>> {
        return this.http.post<WebResponse<boolean>>(`${this.baseUrl}/portal-requests/deny/${userSubId}`, {});
    }

    //TODO(bradley): As of now the request can be processed through the regular endpoints, we might want to have these
    //go through their own specific endpoints so we don't have multiple codepaths through one endpoint. Right now we are
    //not sending back the type of request (unconfirmed vs confirmed) in the modal and we can't easily pass it at the moment
    //due to possibly needing to support email query strings. Won't be too hard to set up if we need to but there is no
    //immediate need.
    /*
    approveReferralPortalRequest$(userSubId: string, info: WorkCompUserInfoUpdateRequest): Observable<WebResponse<boolean>> {
      return this.http.post<WebResponse<boolean>>(`${this.baseUrl}/portal-requests/referral-changes/confirm/${userSubId}`, info);
    }

    denyReferralChangeRequest$(userSubId: string): Observable<WebResponse<boolean>> {
      return this.http.post<WebResponse<boolean>>(`${this.baseUrl}/portal-requests/referral-changes/deny/${userSubId}`, {});
    }*/

    updateReferralUserInfoForPortalRequest(
      userSubId: string,
      updatedInfo: ReferralUser): Observable<WebResponse<boolean>> {
      return this.http.put<WebResponse<boolean>>(`${this.baseUrl}/portal-requests/referral-user/${userSubId}/update-info`, updatedInfo);
    }

    updateWorkCompUserInfoForPortalRequest(
      userSubId: string,
      updatedInfo: WorkCompUserInfoUpdateRequest): Observable<WebResponse<boolean>> {
        return this.http.put<WebResponse<boolean>>(`${this.baseUrl}/portal-requests/workcomp-user/${userSubId}/update-info`, updatedInfo);
    }

    updateWorkCompUserInfoForUserMgmt(userSubId: string, updatedInfo: WorkCompUserInfoUpdateRequest): Observable<WebResponse<boolean>> {
        return this.http.put<WebResponse<boolean>>(`${this.baseUrl}/user-mgmt/workcomp-user/${userSubId}/update-info`, updatedInfo);
    }

    updateReferralUserInfoForUserMgmt(userSubId: string, updatedInfo: ReferralUser): Observable<WebResponse<boolean>> {
      return this.http.put<WebResponse<boolean>>(`${this.baseUrl}/user-mgmt/referral-user/${userSubId}/update-info`, updatedInfo);
    }

    updateProviderGroup(providerId: string, groupName: string | null): Observable<WebResponse<boolean>> {
      let params = {};
      if(groupName) params = {groupName: groupName};
      return this.http.put<WebResponse<boolean>>(`${this.baseUrl}/provider-mgmt/${providerId}`, {}, {params: params});
    }

    refreshProviders(): Observable<void> {
      return this.http.post<void>(`${this.baseUrl}/provider-mgmt/refresh`, {});
    }

    updateAdminUserInfoForUserMgmt(userSubId: string, updatedInfo: AdminInfoUpdateRequest): Observable<WebResponse<boolean>> {
        return this.http.put<WebResponse<boolean>>(`${this.baseUrl}/user-mgmt/admin-user/${userSubId}/update-info`, updatedInfo);
    }

    updateAdminUserAccountSettings(userId: string, updatedInfo: User): Observable<SaveResult> {
        return this.http.put<SaveResult>(`${this.baseUrl}/${userId}/settings-details`, updatedInfo);
    }

    getAllSystemUsers$(
      userPageRequest: UserPageRequest): Observable<WebResponse<PaginatedResult<UserStatusInfo>>> {
        return this.http.post<WebResponse<PaginatedResult<UserStatusInfo>>>
        (`${this.baseUrl}/user-list/get-all-system-users`, userPageRequest);
    }

    suspendUser$(userSubId: string): Observable<WebResponse<boolean>> {
        return this.http.post<WebResponse<boolean>>(`${this.baseUrl}/user-mgmt/suspend-user/${userSubId}`, {});
    }

    reActivateUser$(userSubId: string): Observable<WebResponse<boolean>> {
        return this.http.post<WebResponse<boolean>>(`${this.baseUrl}/user-mgmt/reactivate-user/${userSubId}`, {});
    }

    deleteUser$(userSubId: string): Observable<WebResponse<boolean>> {
        return this.http.delete<WebResponse<boolean>>(`${this.baseUrl}/user-mgmt/delete-user/${userSubId}`);
    }

    invalidateImpersonationToken(refreshToken: string): Observable<Response> {
      return this.http.post<Response>(`${this.baseUrl}/endImpersonation`, { refreshToken });
    }

    impersonate$(userId: string): Observable<AuthResponse> {
      return this.http.post<AuthResponse>(`${this.baseUrl}/impersonate/${userId}`, {});
    }
}
